import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageSizeSelectV8, { readPageSizeV8 } from './PageSizeSelectV8';
import {
  Button,
  Dropdown,
  Form,
  Icon,
  Label,
  Loader,
  Pagination,
  Popup,
  Table,
} from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import {
  API,
  copy,
  isRoot,
  showError,
  showSuccess,
  showWarning,
  timestamp2string,
} from '../helpers';

import {
  useSessionViewFieldV81,
  useSessionViewStateV81,
} from '../hooks/useSessionViewStateV81';
import RootStepUpModalV815 from './RootStepUpModalV815';

function renderTimestamp(timestamp) {
  return <>{timestamp2string(timestamp)}</>;
}

export function formatTokenMoneyV97(value) {
  if (typeof value !== 'string' || !/^-?\d+$/.test(value)) return null;
  const negative = value.startsWith('-');
  const digits = (negative ? value.slice(1) : value).padStart(10, '0');
  const whole = digits.slice(0, -9).replace(/^0+(?=\d)/, '');
  const fraction = digits.slice(-9).replace(/0+$/, '').padEnd(2, '0');
  return `${negative ? '-' : ''}${whole}.${fraction}`;
}

export function formatLegacyQuotaUSDV107(
  value,
  quotaPerDollar = localStorage.getItem('quota_per_unit')
) {
  const rawValue = String(value);
  const rawRate = String(quotaPerDollar);
  if (!/^-?\d+$/.test(rawValue)) return null;
  if (!/^\d+(?:\.\d{1,12})?$/.test(rawRate)) return null;

  const quota = Number(rawValue);
  const rate = Number(rawRate);
  if (!Number.isSafeInteger(quota) || !Number.isFinite(rate) || rate <= 0) {
    return null;
  }

  const nano = Math.round((quota / rate) * 1e9);
  if (!Number.isSafeInteger(nano)) return null;
  return formatManagedUSDV107(String(nano));
}

export function formatManagedUSDV107(value) {
  const formatted = formatTokenMoneyV97(value);
  return formatted === null ? null : `$${formatted}`;
}

export function TokenUsageCellV97({ token, t }) {
  const usage = token.usage_v97 || {};
  const managedValue = formatManagedUSDV107(usage.managed_used_nano_usd);
  if (usage.managed_billing) {
    return (
      <span
        title={t(
          managedValue === null
            ? 'token.usage_v97.unattributed_help'
            : 'token.usage_v97.managed_usd_help'
        )}
      >
        {managedValue || '-'}
      </span>
    );
  }
  const legacyValue = formatLegacyQuotaUSDV107(
    usage.legacy_used_quota ?? token.used_quota
  );
  return (
    <span title={t('token.usage_v97.legacy_usd_help')}>
      {legacyValue || t('token.usage_v97.usd_unavailable')}
    </span>
  );
}

export function TokenRemainingCellV107({ token, t }) {
  const usage = token.usage_v97 || {};
  if (usage.managed_billing) {
    return (
      <span title={t('token.usage_v97.managed_limit_help')}>
        {t('token.usage_v97.managed_limit')}
      </span>
    );
  }
  if (token.unlimited_quota) {
    return <span>{t('token.table.unlimited')}</span>;
  }
  const value = formatLegacyQuotaUSDV107(
    usage.legacy_remain_quota ?? token.remain_quota
  );
  return (
    <span title={t('token.usage_v97.remaining_usd_help')}>
      {value || t('token.usage_v97.usd_unavailable')}
    </span>
  );
}

function renderStatus(status, t) {
  switch (status) {
    case 1:
      return (
        <Label basic color='green'>
          {t('token.table.status_enabled')}
        </Label>
      );
    case 2:
      return (
        <Label basic color='red'>
          {t('token.table.status_disabled')}
        </Label>
      );
    case 3:
      return (
        <Label basic color='yellow'>
          {t('token.table.status_expired')}
        </Label>
      );
    case 4:
      return (
        <Label basic color='grey'>
          {t('token.table.status_depleted')}
        </Label>
      );
    default:
      return (
        <Label basic color='black'>
          {t('token.table.status_unknown')}
        </Label>
      );
  }
}

const TokensTable = ({ targetUserId = null, basePath = '/token' }) => {
  const { t } = useTranslation();
  const tokenApiBase = targetUserId
    ? `/api/token/managed/${targetUserId}`
    : '/api/token';
  const addTokenPath = targetUserId ? `${basePath}/token/add` : '/token/add';
  const editTokenPath = (id) =>
    targetUserId ? `${basePath}/token/edit/${id}` : `/token/edit/${id}`;

  const COPY_OPTIONS = [
    { key: 'raw', text: t('token.copy_options.raw'), value: '' },
    { key: 'next', text: t('token.copy_options.next'), value: 'next' },
    { key: 'ama', text: t('token.copy_options.ama'), value: 'ama' },
    { key: 'opencat', text: t('token.copy_options.opencat'), value: 'opencat' },
    { key: 'lobe', text: t('token.copy_options.lobe'), value: 'lobechat' },
  ];

  const OPEN_LINK_OPTIONS = [
    { key: 'next', text: t('token.copy_options.next'), value: 'next' },
    { key: 'ama', text: t('token.copy_options.ama'), value: 'ama' },
    { key: 'opencat', text: t('token.copy_options.opencat'), value: 'opencat' },
    { key: 'lobe', text: t('token.copy_options.lobe'), value: 'lobechat' },
  ];

  const [tokens, setTokens] = useState([]);
  const [revealedTokensV815, setRevealedTokensV815] = useState({});
  const [stepUpTokenV815, setStepUpTokenV815] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [viewState, setViewState] = useSessionViewStateV81(
    `tokens.${targetUserId || 'self'}`,
    {
      activePage: 1,
      searchKeyword: '',
      orderBy: '',
      pageSize: readPageSizeV8(
        `v8-page-size-tokens-${targetUserId || 'self'}`,
        10
      ),
    }
  );
  const [activePage, setActivePage] = useSessionViewFieldV81(
    viewState,
    setViewState,
    'activePage'
  );
  const [searchKeyword, setSearchKeyword] = useSessionViewFieldV81(
    viewState,
    setViewState,
    'searchKeyword'
  );
  const [searching, setSearching] = useState(false);
  const [orderBy, setOrderBy] = useSessionViewFieldV81(
    viewState,
    setViewState,
    'orderBy'
  );
  const [pageSize, setPageSize] = useSessionViewFieldV81(
    viewState,
    setViewState,
    'pageSize'
  );
  const initialPageRef = useRef(Math.max(1, Number(activePage) || 1));
  const queryConfigRef = useRef({ orderBy, pageSize, tokenApiBase });

  const loadTokens = async (startIdx) => {
    const res = await API.get(
      `${tokenApiBase}?p=${startIdx}&page_size=${pageSize}&order=${orderBy}`
    );
    const { success, message, data } = res.data;
    if (success) {
      setLoadFailed(false);
      if (startIdx === 0) {
        setTokens(data);
      } else {
        let newTokens = [...tokens];
        newTokens.splice(startIdx * pageSize, data.length, ...data);
        setTokens(newTokens);
      }
    } else {
      setLoadFailed(true);
      showError(message);
    }
    setLoading(false);
  };

  const loadTokenWindowV81 = async (pageCount) => {
    const windowSize = pageSize * Math.max(1, pageCount);
    const res = searchKeyword
      ? await API.get(
          `${tokenApiBase}/search?keyword=${encodeURIComponent(searchKeyword)}`
        )
      : await API.get(
          `${tokenApiBase}?p=0&page_size=${windowSize}&order=${orderBy}`
        );
    const { success, message, data } = res.data;
    if (success) {
      setLoadFailed(false);
      setTokens(data);
    } else {
      setLoadFailed(true);
      showError(message);
    }
    setLoading(false);
  };

  const onPaginationChange = (e, { activePage }) => {
    (async () => {
      if (activePage === Math.ceil(tokens.length / pageSize) + 1) {
        // In this case we have to load more data and then append them.
        await loadTokens(activePage - 1, orderBy);
      }
      setActivePage(activePage);
    })();
  };

  const refresh = async () => {
    setLoading(true);
    try {
      await loadTokens(activePage - 1);
    } catch (error) {
      setLoadFailed(true);
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  const revealTokenV815 = async (token) => {
    const res = await API.post('/api/security/root/secrets/reveal', {
      kind: 'token',
      id: token.id,
    });
    if (res.data?.success) {
      const expiresAt = Date.parse(res.data.data?.expires_at);
      setRevealedTokensV815((current) => ({
        ...current,
        [token.id]: {
          key: res.data.data.key,
          expiresAt: Number.isNaN(expiresAt)
            ? Date.now() + 10 * 60 * 1000
            : expiresAt,
        },
      }));
      return;
    }
    if (res.data?.code === 'step_up_required') {
      setStepUpTokenV815(token);
      return;
    }
    showError(res.data?.message);
  };

  useEffect(() => {
    const timer = window.setInterval(() => {
      const now = Date.now();
      setRevealedTokensV815((current) => {
        const next = Object.fromEntries(
          Object.entries(current).filter(([, item]) => item.expiresAt > now)
        );
        return Object.keys(next).length === Object.keys(current).length
          ? current
          : next;
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, []);

  const onCopy = async (type, key) => {
    let status = localStorage.getItem('status');
    let serverAddress = '';
    if (status) {
      status = JSON.parse(status);
      serverAddress = status.server_address;
    }
    if (serverAddress === '') {
      serverAddress = window.location.origin;
    }
    let encodedServerAddress = encodeURIComponent(serverAddress);
    const nextLink = localStorage.getItem('chat_link');
    let nextUrl;

    if (nextLink) {
      nextUrl =
        nextLink + `/#/?settings={"key":"sk-${key}","url":"${serverAddress}"}`;
    } else {
      nextUrl = `https://app.nextchat.dev/#/?settings={"key":"sk-${key}","url":"${serverAddress}"}`;
    }

    let url;
    switch (type) {
      case 'ama':
        url = `ama://set-api-key?server=${encodedServerAddress}&key=sk-${key}`;
        break;
      case 'opencat':
        url = `opencat://team/join?domain=${encodedServerAddress}&token=sk-${key}`;
        break;
      case 'next':
        url = nextUrl;
        break;
      case 'lobechat':
        url =
          nextLink +
          `/?settings={"keyVaults":{"openai":{"apiKey":"sk-${key}","baseURL":"${serverAddress}/v1"}}}`;
        break;
      default:
        url = `sk-${key}`;
    }
    if (await copy(url)) {
      showSuccess(t('token.messages.copy_success'));
    } else {
      showWarning(t('token.messages.copy_failed'));
      setSearchKeyword(url);
    }
  };

  const onOpenLink = async (type, key) => {
    let status = localStorage.getItem('status');
    let serverAddress = '';
    if (status) {
      status = JSON.parse(status);
      serverAddress = status.server_address;
    }
    if (serverAddress === '') {
      serverAddress = window.location.origin;
    }
    let encodedServerAddress = encodeURIComponent(serverAddress);
    const chatLink = localStorage.getItem('chat_link');
    let defaultUrl;

    if (chatLink) {
      defaultUrl =
        chatLink + `/#/?settings={"key":"sk-${key}","url":"${serverAddress}"}`;
    } else {
      defaultUrl = `https://app.nextchat.dev/#/?settings={"key":"sk-${key}","url":"${serverAddress}"}`;
    }
    let url;
    switch (type) {
      case 'ama':
        url = `ama://set-api-key?server=${encodedServerAddress}&key=sk-${key}`;
        break;

      case 'opencat':
        url = `opencat://team/join?domain=${encodedServerAddress}&token=sk-${key}`;
        break;

      case 'lobechat':
        url =
          chatLink +
          `/?settings={"keyVaults":{"openai":{"apiKey":"sk-${key}","baseURL":"${serverAddress}/v1"}}}`;
        break;

      default:
        url = defaultUrl;
    }

    window.open(url, '_blank');
  };

  useEffect(() => {
    const previousConfig = queryConfigRef.current;
    const initialConfig =
      previousConfig.orderBy === orderBy &&
      previousConfig.pageSize === pageSize &&
      previousConfig.tokenApiBase === tokenApiBase;
    const pageCount = initialConfig ? initialPageRef.current : 1;
    queryConfigRef.current = { orderBy, pageSize, tokenApiBase };
    if (!initialConfig) initialPageRef.current = 1;
    setLoading(true);
    loadTokenWindowV81(pageCount)
      .then()
      .catch((reason) => {
        setLoadFailed(true);
        showError(reason);
        setLoading(false);
      });
    // The initial route entry intentionally restores a wider data window.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderBy, pageSize, tokenApiBase]);

  const manageToken = async (id, action, idx) => {
    let data = { id };
    let res;
    switch (action) {
      case 'delete':
        res = await API.delete(`${tokenApiBase}/${id}`);
        break;
      case 'enable':
        data.status = 1;
        res = await API.put(`${tokenApiBase}?status_only=true`, data);
        break;
      case 'disable':
        data.status = 2;
        res = await API.put(`${tokenApiBase}?status_only=true`, data);
        break;
      default:
        return;
    }
    const { success, message } = res.data;
    if (success) {
      showSuccess(t('token.messages.operation_success'));
      let token = res.data.data;
      let newTokens = [...tokens];
      let realIdx = (activePage - 1) * pageSize + idx;
      if (action === 'delete') {
        newTokens[realIdx].deleted = true;
      } else {
        newTokens[realIdx].status = token.status;
      }
      setTokens(newTokens);
    } else {
      showError(message);
    }
  };

  const searchTokens = async () => {
    if (searchKeyword === '') {
      // if keyword is blank, load files instead.
      await loadTokens(0);
      setActivePage(1);
      setOrderBy('');
      return;
    }
    setSearching(true);
    const res = await API.get(
      `${tokenApiBase}/search?keyword=${encodeURIComponent(searchKeyword)}`
    );
    const { success, message, data } = res.data;
    if (success) {
      setTokens(data);
      setActivePage(1);
    } else {
      showError(message);
    }
    setSearching(false);
  };

  const handleKeywordChange = async (e, { value }) => {
    setSearchKeyword(value.trim());
  };

  const sortToken = (key) => {
    if (tokens.length === 0) return;
    setLoading(true);
    let sortedTokens = [...tokens];
    sortedTokens.sort((a, b) => {
      if (!isNaN(a[key])) {
        // If the value is numeric, subtract to sort
        return a[key] - b[key];
      } else {
        // If the value is not numeric, sort as strings
        return ('' + a[key]).localeCompare(b[key]);
      }
    });
    if (sortedTokens[0].id === tokens[0].id) {
      sortedTokens.reverse();
    }
    setTokens(sortedTokens);
    setLoading(false);
  };

  const handleOrderByChange = (e, { value }) => {
    setOrderBy(value);
    setActivePage(1);
  };

  const handlePageSizeChange = (nextPageSize) => {
    setLoading(true);
    setActivePage(1);
    setPageSize(nextPageSize);
  };

  const visibleTokens = tokens
    .slice((activePage - 1) * pageSize, activePage * pageSize)
    .filter((token) => !token.deleted);

  return (
    <div className='token-list-v101' aria-busy={loading || searching}>
      <Form className='token-search-v101' onSubmit={searchTokens}>
        <Form.Input
          icon='search'
          fluid
          iconPosition='left'
          placeholder={t('token.search')}
          aria-label={t('token.search')}
          value={searchKeyword}
          loading={searching}
          onChange={handleKeywordChange}
        />
      </Form>

      <p className='token-usage-note-v101' role='note'>
        <Icon name='info circle' />
        <span>{t('token.usage_v97.partial')}</span>
      </p>

      <div
        className='management-table-scroll-v72'
        role='region'
        aria-label='Scrollable data table'
        tabIndex={0}
      >
        <Table
          basic={'very'}
          compact
          size='small'
          unstackable
          className='management-table-v72 management-token-table-v72'
        >
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  sortToken('name');
                }}
              >
                {t('token.table.name')}
              </Table.HeaderCell>
              <Table.HeaderCell
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  sortToken('status');
                }}
              >
                {t('token.table.status')}
              </Table.HeaderCell>
              <Table.HeaderCell
                title={t('token.usage_v97.spent_usd_help')}
              >
                {t('token.usage_v97.spent_usd')}
              </Table.HeaderCell>
              <Table.HeaderCell>
                {t('token.usage_v97.remaining_usd')}
              </Table.HeaderCell>
              <Table.HeaderCell
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  sortToken('created_time');
                }}
              >
                {t('token.table.created_time')}
              </Table.HeaderCell>
              <Table.HeaderCell
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  sortToken('expired_time');
                }}
              >
                {t('token.table.expired_time')}
              </Table.HeaderCell>
              <Table.HeaderCell>{t('token.table.actions')}</Table.HeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {visibleTokens.length === 0 && (
              <Table.Row>
                <Table.Cell colSpan={7} className='token-table-state-v101'>
                  <div className='token-table-state-content-v101'>
                    {loading ? (
                      <Loader active inline='centered' size='small'>
                        {t('token.layout_v101.loading', {
                          defaultValue: 'Loading tokens...',
                        })}
                      </Loader>
                    ) : (
                      <span role='status'>
                        {loadFailed
                          ? t('token.layout_v101.failed', {
                              defaultValue:
                                'Unable to load tokens. Please refresh.',
                            })
                          : t('token.layout_v101.empty', {
                              defaultValue: 'No tokens found',
                            })}
                      </span>
                    )}
                  </div>
                </Table.Cell>
              </Table.Row>
            )}
            {tokens
              .slice((activePage - 1) * pageSize, activePage * pageSize)
              .map((token, idx) => {
                if (token.deleted) return <></>;
                const codingLockedV110 = token.managed_kind_v110 === 'coding_shared';
                const revealedV815 = revealedTokensV815[token.id];
                const effectiveKeyV815 = revealedV815?.key || token.key;
                const maskedV815 = Boolean(
                  token.key_masked && !effectiveKeyV815
                );

                const copyOptionsWithHandlers = COPY_OPTIONS.map((option) => ({
                  ...option,
                  onClick: async () => {
                    await onCopy(option.value, effectiveKeyV815);
                  },
                }));

                const openLinkOptionsWithHandlers = OPEN_LINK_OPTIONS.map(
                  (option) => ({
                    ...option,
                    onClick: async () => {
                      await onOpenLink(option.value, effectiveKeyV815);
                    },
                  })
                );

                return (
                  <Table.Row key={token.id}>
                    <Table.Cell>
                      {token.name ? token.name : t('token.table.no_name')}
                    </Table.Cell>
                    <Table.Cell style={{ whiteSpace: 'nowrap' }}>
                      {renderStatus(token.status, t)}
                    </Table.Cell>
                    <Table.Cell>
                      <TokenUsageCellV97 token={token} t={t} />
                    </Table.Cell>

                    <Table.Cell>
                      <TokenRemainingCellV107 token={token} t={t} />
                    </Table.Cell>
                    <Table.Cell>
                      {renderTimestamp(token.created_time)}
                    </Table.Cell>
                    <Table.Cell>
                      {token.expired_time === -1
                        ? t('token.table.never_expire')
                        : renderTimestamp(token.expired_time)}
                    </Table.Cell>
                    <Table.Cell>
                      {codingLockedV110 ? (
                        <span data-testid='coding-key-locked-v110'>
                          <Icon name='lock' />
                          {t('token.coding_locked_v110', { defaultValue: 'Coding apps · Locked' })}
                        </span>
                      ) : <div className='token-row-actions-v101'>
                        {isRoot() && token.key_masked && (
                          <div
                            className='token-secret-review-v101'
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              marginBottom: '6px',
                              flexWrap: 'wrap',
                            }}
                          >
                            <code style={{ wordBreak: 'break-all' }}>
                              {revealedV815
                                ? 'sk-' + effectiveKeyV815
                                : token.key_display || '********'}
                            </code>
                            <Button
                              basic
                              primary
                              size='mini'
                              icon='eye'
                              content='Review'
                              onClick={() => revealTokenV815(token)}
                            />
                          </div>
                        )}
                        <Button.Group color='green' size={'tiny'}>
                          <Button
                            size={'tiny'}
                            positive
                            disabled={maskedV815}
                            onClick={async () =>
                              await onCopy('', effectiveKeyV815)
                            }
                          >
                            {t('token.buttons.copy')}
                          </Button>
                          <Dropdown
                            className='button icon'
                            floating
                            disabled={maskedV815}
                            options={copyOptionsWithHandlers}
                            trigger={<></>}
                          />
                        </Button.Group>{' '}
                        <Button.Group color='olive' size={'tiny'}>
                          <Button
                            size={'tiny'}
                            positive
                            disabled={maskedV815}
                            onClick={() => onOpenLink('', effectiveKeyV815)}
                          >
                            {t('token.buttons.chat')}
                          </Button>
                          <Dropdown
                            className='button icon'
                            floating
                            disabled={maskedV815}
                            options={openLinkOptionsWithHandlers}
                            trigger={<></>}
                          />
                        </Button.Group>{' '}
                        <Popup
                          trigger={
                            <Button
                              size='mini'
                              negative
                              icon='trash alternate outline'
                              title={t('token.buttons.delete')}
                              aria-label={t('token.buttons.delete')}
                            />
                          }
                          on='click'
                          flowing
                          hoverable
                        >
                          <Button
                            size={'tiny'}
                            negative
                            onClick={() => {
                              manageToken(token.id, 'delete', idx);
                            }}
                          >
                            {t('token.buttons.confirm_delete')} {token.name}
                          </Button>
                        </Popup>
                        <div className='token-management-actions-v102'>
                          <Button
                            size='tiny'
                            icon={token.status === 1 ? 'pause' : 'play'}
                            content={t(
                              token.status === 1
                                ? 'token.buttons.disable'
                                : 'token.buttons.enable'
                            )}
                            title={t(
                              token.status === 1
                                ? 'token.buttons.disable'
                                : 'token.buttons.enable'
                            )}
                            aria-label={t(
                              token.status === 1
                                ? 'token.buttons.disable'
                                : 'token.buttons.enable'
                            )}
                            disabled={
                              token.status !== 1 &&
                              token.model_scope === 'custom' &&
                              !token.models?.trim()
                            }
                            onClick={() => {
                              manageToken(
                                token.id,
                                token.status === 1 ? 'disable' : 'enable',
                                idx
                              );
                            }}
                          />
                          <Button
                            size='tiny'
                            as={Link}
                            to={editTokenPath(token.id)}
                            icon='pencil'
                            content={t('token.buttons.settings')}
                            title={t('token.buttons.settings')}
                            aria-label={t('token.buttons.settings')}
                          />
                        </div>
                      </div>}
                    </Table.Cell>
                  </Table.Row>
                );
              })}
          </Table.Body>
        </Table>
      </div>
      <div className='token-list-footer-v101'>
        <div className='token-list-actions-v101'>
          <Button
            size='small'
            primary
            className='token-create-v101'
            as={Link}
            to={addTokenPath}
          >
            <Icon name='plus' />
            {t('token.buttons.add')}
          </Button>
          <Button
            size='small'
            icon='refresh'
            title={t('token.buttons.refresh')}
            aria-label={t('token.buttons.refresh')}
            onClick={refresh}
            loading={loading}
            disabled={loading}
          />
          <Dropdown
            className='token-order-v101'
            placeholder={t('token.sort.placeholder')}
            aria-label={t('token.sort.placeholder')}
            selection
            options={[
              { key: '', text: t('token.sort.default'), value: '' },
              {
                key: 'remain_quota',
                text: t('token.sort.by_remain'),
                value: 'remain_quota',
              },
              {
                key: 'used_quota',
                text: t('token.usage_v97.spent_usd'),
                value: 'used_quota',
              },
            ]}
            value={orderBy}
            onChange={handleOrderByChange}
          />
        </div>
        <div className='token-list-pagination-v101'>
          <PageSizeSelectV8
            label={t('log.table.page_size')}
            storageKey={`v8-page-size-tokens-${targetUserId || 'self'}`}
            value={pageSize}
            onChange={handlePageSizeChange}
          />
          <Pagination
            activePage={activePage}
            onPageChange={onPaginationChange}
            size='small'
            siblingRange={1}
            totalPages={
              Math.ceil(tokens.length / pageSize) +
              (tokens.length % pageSize === 0 ? 1 : 0)
            }
          />
        </div>
      </div>
      <RootStepUpModalV815
        open={Boolean(stepUpTokenV815)}
        onClose={() => setStepUpTokenV815(null)}
        onVerified={() => {
          const selected = stepUpTokenV815;
          setStepUpTokenV815(null);
          if (selected) revealTokenV815(selected);
        }}
      />
    </div>
  );
};

export default TokensTable;






